// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties.
 *
 * On product pages the input is submitted with the buy-buttons form as a
 * line-item property. On the cart page (detected via `data-cart-attribute`
 * on the input or `data-cart-checkbox-attribute` on a wrapper) the value is
 * persisted to the cart attributes via /cart/update.js so it appears on the
 * order under "Additional details".
 *
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #cartUpdateTimer = undefined;
  #CART_UPDATE_DEBOUNCE_MS = 500;

  connectedCallback() {
    super.connectedCallback?.();
    this.#bindCartCheckbox();
  }

  handleInput() {
    this.#updateCharacterCount();
    this.#scheduleCartUpdateFromTextInput();
  }

  #scheduleCartUpdateFromTextInput() {
    const input = this.refs?.textInput;
    const attrName = input?.dataset?.cartAttribute;
    if (!input || !attrName) return;

    if (this.#cartUpdateTimer !== undefined) clearTimeout(this.#cartUpdateTimer);
    this.#cartUpdateTimer = setTimeout(() => {
      this.#updateCartAttribute(attrName, input.value);
    }, this.#CART_UPDATE_DEBOUNCE_MS);
  }

  #bindCartCheckbox() {
    const wrapper = /** @type {HTMLElement | null} */ (this.querySelector('[data-cart-checkbox-attribute]'));
    if (!wrapper) return;
    const checkbox = /** @type {HTMLInputElement | null} */ (wrapper.querySelector('input[type="checkbox"]'));
    if (!checkbox || checkbox.dataset.cartAttrBound === '1') return;
    checkbox.dataset.cartAttrBound = '1';

    const attrName = wrapper.dataset.cartCheckboxAttribute;
    const checkedValue = wrapper.dataset.cartCheckboxValue || 'yes';
    if (!attrName) return;
    checkbox.addEventListener('change', () => {
      this.#updateCartAttribute(attrName, checkbox.checked ? checkedValue : '');
    });
  }

  /**
   * @param {string} name
   * @param {string} value
   */
  #updateCartAttribute(name, value) {
    if (!name) return;
    /** @type {Record<string, string>} */
    const attributes = {};
    attributes[name] = value;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).catch((err) => {
      console.error('Cart attribute update failed:', err);
    });
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    if (!characterCount || !textInput) return;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
